import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { loadBooks } from "../services/bookService";
import { Book } from "../types/Book";
import BookCard from "./BookCard";
import BuildInfo from "./BuildInfo";
import LoadingSpinner from "./LoadingSpinner";
import ErrorBoundary from "./ErrorBoundary";
import analytics from "../services/analytics";

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [books, setBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBooks = async () => {
      try {
        setLoading(true);
        const loadedBooks = await loadBooks();
        setBooks(loadedBooks);
        setError(null);
        
        // Track homepage view
        analytics.trackHomepageViewed();
      } catch (err) {
        console.error("Error loading books:", err);
        setError("Failed to load books. Please try again later.");
        
        // Track error
        analytics.trackError("Failed to load books", "HomePage");
      } finally {
        setLoading(false);
      }
    };

    fetchBooks();
  }, []);

  const handleBookSelect = (bookId: string, bookTitle: string, position: number) => {
    // Track book card click
    analytics.trackBookCardClicked(bookId, bookTitle, position);
    navigate(`/book/${bookId}`);
  };

  return (
    <ErrorBoundary 
      fallback={
        <div className="error-boundary">
          <div className="error-boundary-content">
            <div className="error-boundary-icon">📚</div>
            <h2 className="error-boundary-title">Unable to Load Stories</h2>
            <p className="error-boundary-message">
              We're having trouble connecting to our story database. Please check your internet connection and try again.
            </p>
            <div className="error-boundary-actions">
              <button 
                className="error-boundary-button primary"
                onClick={() => window.location.reload()}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      }
    >
      <div className="home-page">
      <header className="home-header">
        <h1>Voyager</h1>
        <h2>Stories, Brought to Life</h2>
        <p className="tagline">
          Discover original short stories and books by independent creators. Listen anywhere, anytime.
        </p>
      </header>

      <main className="main-content">
        <section className="featured-section">
          <h2>Featured Stories</h2>
          {loading && (
            <LoadingSpinner 
              message="Loading stories..." 
              size="large"
            />
          )}
          {error && (
            <div className="error-message">
              <p>{error}</p>
            </div>
          )}
          {!loading && !error && (
            <div className="books-grid">
              {books.map((book, index) => (
                <BookCard
                  key={book.id}
                  book={book}
                  onSelect={(bookId) => handleBookSelect(bookId, book.title, index)}
                />
              ))}
            </div>
          )}
        </section>

        <section className="info-section">
          <div className="info-cards">
            <div className="info-card">
              <h3>Fresh Stories, Every Day</h3>
              <p>
                From fantasy epics to bite-sized mysteries, there's always something new to discover.
              </p>
            </div>
            <div className="info-card">
              <h3>Made for Modern Life</h3>
              <p>
                Whether on your commute, workout, or wind-down, stories fit into your day.
              </p>
            </div>
            <div className="info-card">
              <h3>Powered by Creators</h3>
              <p>
                Your listening directly supports writers.
              </p>
            </div>
          </div>
        </section>
      </main>
      
      <BuildInfo />
      </div>
    </ErrorBoundary>
  );
};

export default HomePage;
